import React, { useState } from "react";

import { Formik, Form, FormikHelpers } from "formik";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import { STRING } from "@constants";
import {
  UNIT_CATEGORY_FIELDS,
  UNIT_CATEGORY_INITIAL_VALUES,
} from "./constants";

import { UnitCategoryRead, UnitCategoryUpdate } from "@types";
import { UseManageSizesFormProps } from "./types";

import { UnitCategorySchema } from "./utils";

import { useManageForm } from "@hooks";
import {
  useCreateUnitCategory,
  useUpdateUnitCategory,
  useUploadUnitCategoryVideo,
} from "@api";

import { MediaUploader, SimpleTextField } from "@components";

export function UnitCategoryForm({
  editValues,
  onClose,
}: UseManageSizesFormProps) {
  const { triggerCreateUnitCategory } = useCreateUnitCategory();
  const { triggerUpdateUnitCategory } = useUpdateUnitCategory();
  const { triggerUploadVideo, loading: uploadingVideo } =
    useUploadUnitCategoryVideo();

  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);

  const { sendForm } = useManageForm<UnitCategoryUpdate, UnitCategoryRead>({
    createFn: (data) => triggerCreateUnitCategory(data as UnitCategoryRead),
    updateFn: (id, data) => triggerUpdateUnitCategory({ id, payload: data }),
    successMessage: {
      create: "Unit Category successfully created",
      update: "Unit Category successfully updated",
    },
  });

  const initialValues: UnitCategoryUpdate = editValues
    ? { ...editValues }
    : UNIT_CATEGORY_INITIAL_VALUES;

  const handleCancel = (dirty: boolean, resetForm: () => void) => {
    if (!dirty) return onClose();

    confirmDialog({
      message: STRING.UNIT_CATEGORY_FORM.CONFIRM_DIALOG_MESSAGE,
      header: STRING.UNIT_CATEGORY_FORM.CONFIRM_DIALOG_HEADER,
      icon: STRING.UNIT_CATEGORY_FORM.CONFIRM_DIALOG_ICON,
      acceptClassName: STRING.UNIT_CATEGORY_FORM.CONFIRM_DIALOG_ACCEPT_CLASS,
      accept: () => {
        resetForm();
        onClose();
      },
    });
  };

  const handleSubmit = async (
    values: UnitCategoryUpdate,
    formikHelpers: FormikHelpers<UnitCategoryUpdate>,
  ) => {
    const { videoUrl, createdAt, updatedAt, ...rest } = values;

    const savedCategory = await sendForm(rest);

    if (selectedVideoFile && savedCategory?.id) {
      const updated = await triggerUploadVideo({
        id: savedCategory.id,
        file: selectedVideoFile,
      });

      if (updated?.videoUrl) {
        formikHelpers.setFieldValue("videoUrl", updated.videoUrl);
      }
    }

    formikHelpers.resetForm();
    onClose();
  };

  return (
    <>
      <ConfirmDialog />
      <Formik
        initialValues={initialValues}
        validationSchema={UnitCategorySchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange
      >
        {({ isSubmitting, dirty, resetForm, values }) => (
          <Form className="flex flex-column h-full">
            <div className="flex-1 overflow-auto px-3 bg-surface-50">
              <MediaUploader
                label={STRING.UNIT_CATEGORY_VIDEO_UPLOADER.TITLE}
                initialUrl={values.videoUrl}
                accept="video/*"
                noFileText={STRING.UNIT_CATEGORY_VIDEO_UPLOADER.NO_VIDEO}
                selectedText={STRING.UNIT_CATEGORY_VIDEO_UPLOADER.SELECTED}
                onFileSelect={(file) => setSelectedVideoFile(file)}
              />
              {UNIT_CATEGORY_FIELDS.map((field) => (
                <div key={field.name} className="col-12 px-1">
                  <p className="text-sm text-surface-500 mb-1 font-medium">
                    {field.label}
                  </p>

                  <SimpleTextField
                    id={field.id}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}
            </div>

            <div className="surface-0 border-top-1 mt-4 surface-border py-4 px-4 flex justify-content-end gap-3">
              <Button
                type="button"
                label={STRING.UNIT_CATEGORY_FORM.CANCEL_BUTTON}
                icon={STRING.UNIT_CATEGORY_FORM.CANCEL_ICON}
                className="p-button-outlined p-button-secondary"
                onClick={() => handleCancel(dirty, resetForm)}
              />
              <Button
                type="submit"
                label={
                  editValues
                    ? STRING.UNIT_CATEGORY_FORM.UPDATE_BUTTON
                    : STRING.UNIT_CATEGORY_FORM.CREATE_BUTTON
                }
                icon={
                  editValues
                    ? STRING.UNIT_CATEGORY_FORM.UPDATE_ICON
                    : STRING.UNIT_CATEGORY_FORM.CREATE_ICON
                }
                className="p-button-primary"
                loading={isSubmitting || uploadingVideo}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
